import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigation } from '@react-navigation/native'
import { View } from 'react-native'
import { Button } from 'react-native-paper'
import {
  useUserTimelineEventsQuery,
  TimeLineAction,
} from '../../../../generated/graphql'
import {
  TimelineList,
  TimelineResultItem,
} from '../../collaborators/components/timeline-base.list'
import { timelineActionsAvailableForFilters } from '../../collaborators/timeline-excluded-event-types'
import { useAuthContext } from '../../../../contexts/auth-context'
import { AppNavigation } from '../../../../navigation/types'

const TIMELINE_PAGE_SIZE = 20

type UserTimeLineEventsProps = {
  userId: string
}

export const UserTimeLineEvents: React.FC<UserTimeLineEventsProps> = ({
  userId,
}) => {
  const { t } = useTranslation(['nodes', 'profile'])
  const navigation = useNavigation<AppNavigation>()
  const { isAnonymous, openLoginModal } = useAuthContext()
  const [selectedAction, setSelectedAction] = useState<TimeLineAction | null>(
    null
  )
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)

  const actionsForQuery = useMemo((): TimeLineAction[] => {
    if (selectedAction != null) {
      return [selectedAction]
    }
    return timelineActionsAvailableForFilters()
  }, [selectedAction])

  const { data, loading, refetch, fetchMore } = useUserTimelineEventsQuery({
    variables: {
      userId,
      pagination: {
        offset: 0,
        limit: TIMELINE_PAGE_SIZE,
      },
      actions: actionsForQuery,
      nodeId: selectedNodeId || undefined,
    },
  })

  const results = (data?.userTimelineEvents?.results ??
    []) as TimelineResultItem[]
  const total = data?.userTimelineEvents?.total ?? 0

  const handleRefresh = useCallback(async () => {
    await refetch()
  }, [refetch])

  const handleLoadMore = useCallback(() => {
    if (results.length >= total || loading || results.length === 0) return
    const nextOffset = results.length
    fetchMore({
      variables: {
        userId,
        pagination: {
          offset: nextOffset,
          limit: TIMELINE_PAGE_SIZE,
        },
        actions: actionsForQuery,
        nodeId: selectedNodeId || undefined,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult?.userTimelineEvents) return prev
        const prevResults = prev.userTimelineEvents.results
        const nextResults = fetchMoreResult.userTimelineEvents.results
        return {
          ...prev,
          userTimelineEvents: {
            ...prev.userTimelineEvents,
            total: fetchMoreResult.userTimelineEvents.total,
            results: [...prevResults, ...nextResults],
          },
        }
      },
    })
  }, [
    userId,
    results.length,
    total,
    loading,
    fetchMore,
    actionsForQuery,
    selectedNodeId,
  ])

  if (isAnonymous) {
    return (
      <View>
        <Button mode="contained" onPress={() => openLoginModal(navigation)}>
          {t('profile:login-to-see-timeline')}
        </Button>
      </View>
    )
  }

  return (
    <TimelineList
      results={results}
      total={total}
      loading={loading}
      onRefresh={handleRefresh}
      onLoadMore={handleLoadMore}
      selectedAction={selectedAction}
      onActionFilterSelect={setSelectedAction}
      showNode={true}
      selectedNodeId={selectedNodeId}
      onNodeFilterSelect={setSelectedNodeId}
      emptyMessage={t('empty-item') || 'No events yet'}
    />
  )
}

export default UserTimeLineEvents
